import { useState, useEffect, useCallback } from 'react';

export type Theme = 'dark' | 'light';
export type ThemeSetting = 'dark' | 'light' | 'system';

interface UseThemeOptions {
    /** The default theme setting from the scheduling link */
    defaultTheme?: ThemeSetting;
    /** Unique identifier for localStorage (typically the link slug) */
    storageKey?: string;
}

/**
 * Hook to manage theme state for public booking pages
 * 
 * Priority order for theme:
 * 1. URL parameter (?theme=light or ?theme=dark)
 * 2. User preference stored in localStorage (per link)
 * 3. Link's default theme setting
 * 4. Fallback to 'dark'
 * 
 * When defaultTheme is 'system', it respects the user's OS preference
 */
export function useTheme({ defaultTheme = 'dark', storageKey }: UseThemeOptions = {}) {
    const [theme, setTheme] = useState<Theme>('dark');
    const [isInitialized, setIsInitialized] = useState(false);

    // Get the effective localStorage key
    const getStorageKey = useCallback(() => {
        return storageKey ? `letsmeet-theme-${storageKey}` : 'letsmeet-theme';
    }, [storageKey]);

    // Resolve 'system' theme to actual light/dark
    const resolveSystemTheme = useCallback((): Theme => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
        }
        return 'dark';
    }, []);

    // Determine initial theme
    useEffect(() => {
        // 1. Check URL parameter (highest priority)
        const urlParams = new URLSearchParams(window.location.search);
        const urlTheme = urlParams.get('theme') as Theme | null;
        
        if (urlTheme === 'light' || urlTheme === 'dark') {
            setTheme(urlTheme);
            setIsInitialized(true);
            return;
        }

        // 2. Check localStorage for user preference
        const key = getStorageKey();
        const storedTheme = localStorage.getItem(key) as Theme | null;
        
        if (storedTheme === 'light' || storedTheme === 'dark') {
            setTheme(storedTheme);
            setIsInitialized(true);
            return;
        }

        // 3. Use link's default theme
        if (defaultTheme === 'system') {
            setTheme(resolveSystemTheme());
        } else {
            setTheme(defaultTheme);
        }
        setIsInitialized(true);
    }, [defaultTheme, getStorageKey, resolveSystemTheme]);

    // Listen for system theme changes if defaultTheme is 'system'
    useEffect(() => {
        if (defaultTheme !== 'system') return;
        
        // Check if user hasn't set a preference
        const key = getStorageKey();
        const storedTheme = localStorage.getItem(key);
        if (storedTheme) return; // User has preference, don't auto-switch

        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
        
        const handleChange = (e: MediaQueryListEvent) => {
            setTheme(e.matches ? 'light' : 'dark');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [defaultTheme, getStorageKey]);

    // Apply theme class to body
    useEffect(() => {
        if (!isInitialized) return;

        // Remove both classes first
        document.body.classList.remove('theme-light', 'theme-dark');
        
        // Add the appropriate class
        document.body.classList.add(`theme-${theme}`);

        return () => {
            document.body.classList.remove('theme-light', 'theme-dark');
        };
    }, [theme, isInitialized]);

    // Toggle theme function
    const toggleTheme = useCallback(() => {
        const newTheme: Theme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        
        // Persist to localStorage
        const key = getStorageKey();
        localStorage.setItem(key, newTheme);
    }, [theme, getStorageKey]);

    // Set theme explicitly
    const setThemeExplicit = useCallback((newTheme: Theme) => {
        setTheme(newTheme);
        
        // Persist to localStorage
        const key = getStorageKey();
        localStorage.setItem(key, newTheme);
    }, [getStorageKey]);

    // Clear user preference (revert to default)
    const clearPreference = useCallback(() => {
        const key = getStorageKey();
        localStorage.removeItem(key);
        
        // Apply default theme
        if (defaultTheme === 'system') {
            setTheme(resolveSystemTheme());
        } else {
            setTheme(defaultTheme);
        }
    }, [defaultTheme, getStorageKey, resolveSystemTheme]);

    return {
        theme,
        toggleTheme,
        setTheme: setThemeExplicit,
        clearPreference,
        isDark: theme === 'dark',
        isLight: theme === 'light',
        isInitialized
    };
}

export default useTheme;

